<template>
  <div class="personal-data">
    <h1 class="personal-data__title page-title">
      {{ onboardingMode ? "Enter your personal data" : "Personal data" }}
    </h1>

    <div class="personal-data__card card" v-if="userContactInfo">
      <div class="personal-data__background">
        <profile-image 
          :userId="currentUserId"
          :isEditing="true"
          @imageChanged="onImageChanged"/>
      </div>

      <el-avatar class="personal-data__avatar" :size="120">
        <user-avatar 
          ref="userAvatar"
          :userId="currentUserId" 
          :onClick="openAvatarFilePicker"/>
      </el-avatar>

      <image-uploader
        :entity-id="currentUserId"
        :showModal="showAvatarModal"
        @imageChanged="onAvatarChanged"
        @closeModal="showAvatarModal = false"
      />

      <el-form
        :model="personalDataForm"
        :rules="rules"
        ref="personalDataForm"
        class="personal-data__form"
        label-position="top"
      >
        <div class="row">
          <div class="col col-xs-12 col-md-6">
            <el-form-item
              label="First name"
              prop="firstName"
              :error="errors.firstName"
            >
              <el-input v-model="personalDataForm.firstName"></el-input>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item
              label="Last name"
              prop="lastName"
              :error="errors.lastName"
            >
              <el-input v-model="personalDataForm.lastName"></el-input>
            </el-form-item>
          </div>

          <div class="col col-xs-12">
            <el-form-item
              label="Profile URL"
              prop="link"
              :error="errors.link"
            >
              <el-input v-model="personalDataForm.link"></el-input>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item
              label="Date of Birth"
              prop="birthday"
              :error="errors.birthday"
            >
              <el-date-picker
                class="wide"
                v-model="personalDataForm.birthday"
                type="date"
                placeholder="Pick your birthday"
                format="DD/MM/YYYY"
              >
              </el-date-picker>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item label="Gender" prop="gender" :error="errors.gender">
              <el-radio-group v-model="personalDataForm.gender">
                <el-radio value="1">male</el-radio>
                <el-radio value="2">female</el-radio>
              </el-radio-group>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item
              label="Phone number"
              prop="phoneNumber"
              :error="errors.phoneNumber"
            >
              <el-input v-model="personalDataForm.phoneNumber"></el-input>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item label="Email" prop="email" :error="errors.email">
              <el-input v-model="personalDataForm.email"></el-input>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item
              label="Country"
              prop="country"
              :error="errors.country"
            >
              <el-input v-model="personalDataForm.country"></el-input>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item label="City" prop="city" :error="errors.city">
              <el-input v-model="personalDataForm.city"></el-input>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item
              label="Address"
              prop="address"
              :error="errors.address"
            >
              <el-input v-model="personalDataForm.address"></el-input>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item
              label="Post code"
              prop="postCode"
              :error="errors.postCode"
            >
              <el-input v-model="personalDataForm.postCode"></el-input>
            </el-form-item>
          </div>

          <div class="col col-xs-12">
            <el-form-item
              label="Place of work"
              prop="workPlace"
              :error="errors.workPlace"
            >
              <el-input v-model="personalDataForm.workPlace"></el-input>
            </el-form-item>
          </div>

          <div class="col col-xs-12">
            <el-form-item
              label="Personal website"
              prop="webSite"
              :error="errors.webSite"
            >
              <el-input v-model="personalDataForm.webSite"></el-input>
            </el-form-item>
          </div>

          <div class="col col-xs-12">
            <el-form-item
              prop="extraCV"
              :error="errors.extraCV"
              label="About me"
            >
              <el-input
                type="textarea"
                :rows="5"
                v-model="personalDataForm.extraCV"
              ></el-input>
            </el-form-item>
          </div>
        </div>
      </el-form>

      <div v-if="!onboardingMode" class="personal-data__buttons">
        <el-button
          class="personal-data__cancel-button"
          type="primary"
          @click="handleCancelEditing"
        >
          Cancel
        </el-button>
        <el-button
          type="primary"
          @click="discardChanges"
          :disabled="isButtonDisabled('save-data') || !isDataUpdated"
        >
          Discard changes
        </el-button>
        <el-button
          type="primary"
          @click="validateForm"
          :disabled="isButtonDisabled('save-data')"
        >
          Save changes
        </el-button>
      </div>
    </div>

    <edit-social-network
      v-if="!onboardingMode"
      ref="sociasNetworks"
      :socialNetworks="socialNetworks"
      @update-data="handleUpdateData"
    />

    <delete-account v-if="!onboardingMode"/>
  </div>
</template>

<script>
import { updateUserContactInfo } from '@/api/profile.js';
import {
  isTwoObjectsEqual,
  removeTimezoneOffset,
  parseErrorsByFields,
} from '@/utils/index.js';
import { showMessage } from '@/utils/messages.js';
import { disableButton } from '@/mixins/disableButton.js';
import { uploadImage } from '@/api/images.js';

import EditSocialNetwork from '@/components/profile/EditSocialNetwork.vue';
import DeleteAccount from '@/components/profile/DeleteAccount.vue';
import UserAvatar from '@/components/profile/UserAvatar.vue';
import ProfileImage from '@/components/profile/ProfileImage.vue';
import ImageUploader from '../common/ImageUploader.vue';

export default {
  name: 'PersonalData',
  props: {
    userContactInfo: {
      type: Object,
      required: false,
    },
    onboardingMode: {
      type: Boolean,
      required: false,
      default: false
    },
  },
  mixins: [disableButton],
  components: {
    EditSocialNetwork,
    DeleteAccount,
    UserAvatar,
    ProfileImage,
    ImageUploader
  },
  data() {
    return {
      personalDataForm: {
        link: null,
        firstName: null,
        lastName: null,
        gender: null,
        birthday: null,
        address: null,
        country: null,
        city: null,
        email: null,
        phoneNumber: null,
        postCode: null,
        workPlace: null,
        webSite: null,
        extraCV: null,
        selectedProfileFile: null,
        selectedAvatarFile: null,
      },
      errors: {
        link: null,
        firstName: null,
        lastName: null,
        gender: null,
        birthday: null,
        address: null,
        country: null,
        city: null,
        email: null,
        phoneNumber: null,
        postCode: null,
        workPlace: null,
        webSite: null,
        extraCV: null,
      },
      rules: {
        link: [{ required: false }],
        firstName: [
          {
            required: true,
            message: 'Please input your first name',
            trigger: 'blur',
          },
        ],
        lastName: [
          {
            required: true,
            message: 'Please input your last name ',
            trigger: 'blur',
          },
        ],
        gender: [
          {
            required: true,
            message: 'Please choose your gender',
            trigger: 'change',
          },
        ],
        birthday: [
          {
            required: true,
            message: 'Please input birthday',
            trigger: 'change',
          },
        ],
        email: [
          {
            required: true,
            message: 'Please input your email',
            trigger: 'blur',
          },
          {
            pattern: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            message: 'Email is incorrect',
            trigger: 'blur',
          },
        ],
        phoneNumber: [{ required: false }],
        address: [{ required: false, message: null, trigger: null }],
        country: [
          {
            required: true,
            message: 'Please input your country',
            trigger: 'blur',
          },
        ],
        city: [
          {
            required: true,
            message: 'Please input your city',
            trigger: 'blur',
          },
        ],
        postCode: [{ required: false, message: null, trigger: null }],
        workPlace: [{ required: false, message: null, trigger: null }],
        webSite: [{ required: false, message: null, trigger: null }],
        extraCV: [{ required: false, message: null, trigger: null }],
      },
      showAvatarModal:false,
    };
  },
  computed: {

    currentUserId() {
      return this.$store.state.user.userId;
    },

    isFormFilled() {
      const { personalDataForm } = this;

      const requiredFormFields = {
        firstName: personalDataForm.firstName,
        lastName: personalDataForm.lastName,
        gender: personalDataForm.gender,
        birthday: personalDataForm.birthday,
        country: personalDataForm.country,
        city: personalDataForm.city,
        email: personalDataForm.email,
      };

      return Object.values(requiredFormFields).every((value) => !!value);
    },
    socialNetworks() {
      const { userContactInfo } = this;
      return userContactInfo ? userContactInfo.userSocialNetworks : null;
    },
    oldData() {
      const { userContactInfo } = this;
      return {
        firstName: userContactInfo.firstName,
        lastName: userContactInfo.lastName,
        gender:
          userContactInfo.gender !== null
            ? userContactInfo.gender.toString()
            : null,
        birthday: userContactInfo.birthday,
        address: userContactInfo.address,
        country: userContactInfo.country,
        city: userContactInfo.city,
        email: userContactInfo.email,
        phoneNumber: userContactInfo.phoneNumber,
        postCode: userContactInfo.postCode,
        workPlace: userContactInfo.workPlace,
        webSite: userContactInfo.webSite,
        extraCV: userContactInfo.extraCV,
      };
    },
    isDataUpdated() {
      const { oldData, personalDataForm } = this;
      return !this.isTwoObjectsEqual(oldData, personalDataForm);
    },
  },
  methods: {
    showMessage,
    removeTimezoneOffset,
    isTwoObjectsEqual,
    async requestUpdateUserInfo() {
      this.disableButton('save-data');
      const { removeTimezoneOffset, personalDataForm } = this;

      const dataObject = {
        link: personalDataForm.link,
        firstName: personalDataForm.firstName,
        lastName: personalDataForm.lastName,
        gender: personalDataForm.gender,
        birthday: personalDataForm.birthday
          ? removeTimezoneOffset(personalDataForm.birthday).toISOString()
          : null,
        address: personalDataForm.address,
        country: personalDataForm.country,
        city: personalDataForm.city,
        postCode: personalDataForm.postCode,
        workPlace: personalDataForm.workPlace,
        webSite: personalDataForm.webSite,
        extraCV: personalDataForm.extraCV,
        email: personalDataForm.email,
        phoneNumber: personalDataForm.phoneNumber,
      };
      const { data, success, errors } = await updateUserContactInfo({
        dataObject,
      });
      this.enableButton('save-data');

      if (success) {
        this.showMessage({
          message: 'Your personal data has been successfully updated',
          type: 'success',
        });
        if( this.selectedProfileFile != null) {
          await this.uploadProfileImage();
        }
        if( this.selectedAvatarFile != null) {
          await this.uploadAvatarImage();
        }
        this.handleUpdateData();
      } else if (errors) {
        this.errors = { ...this.errors, ...this.parseErrorsByFields(errors) };
      }
    },
    async uploadProfileImage() {
      try {
        // Use FormData for multipart data
        const formData = new FormData();

        formData.append('entityId', this.currentUserId);
        formData.append('imageType', this.selectedProfileFile.type);
        formData.append('image', this.selectedProfileFile);

        //console.log(formData.get('imageType'));

        const response = await uploadImage(formData, '/api/Profile/UploadProfilePicture');

        this.selectedProfileFile = null;

        // Handle successful upload
        console.log("Image uploaded successfully:", response.data);
      } catch (err) {
        console.error('Error uploading image:', err);
        this.error = "Failed to upload image. Please try again.";
      }
    },

    async uploadAvatarImage() {
      try {
        // Use FormData for multipart data
        const formData = new FormData();

        formData.append('entityId', this.currentUserId);
        formData.append('imageType', this.selectedAvatarFile.type);
        formData.append('image', this.selectedAvatarFile);

        //console.log(formData.get('imageType'));

        const response = await uploadImage(formData, '/api/Profile/UploadAvatar');

        this.selectedAvatarFile = null;

        // Handle successful upload
        console.log("Image uploaded successfully:", response.data);
      } catch (err) {
        console.error('Error uploading image:', err);
        this.error = "Failed to upload image. Please try again.";
      }
    },
    
    parseErrorsByFields,
    discardChanges() {
      const { userSocialNetworks } = this.userContactInfo;

      if (this.$refs.sociasNetworks) {
        this.$refs.sociasNetworks.clearSocialErrors();
      }
      this.$refs.personalDataForm.clearValidate();
      this.setUserData();
    },
    validateForm() {
      const { isFormFilled } = this;

      this.$refs.personalDataForm.validate();

      if (isFormFilled) {
        const { userContactInfo, isDataUpdated } = this;

        if (!isDataUpdated) {
          this.showMessage({
            type: 'error',
            message: 'There are no changes to be saved',
          });
        } else {
          this.requestUpdateUserInfo();
        }
      }
    },
    setUserData() {
      const { userContactInfo } = this;
      this.personalDataForm = {
        link:
          userContactInfo.link === this.currentUserId ? null : userContactInfo.link,
        firstName: userContactInfo.firstName,
        lastName: userContactInfo.lastName,
        gender:
          userContactInfo.gender !== null
            ? userContactInfo.gender.toString()
            : null,
        birthday: userContactInfo.birthday,
        address: userContactInfo.address,
        country: userContactInfo.country,
        city: userContactInfo.city,
        phoneNumber: userContactInfo.phoneNumber,
        postCode: userContactInfo.postCode,
        workPlace: userContactInfo.workPlace,
        webSite: userContactInfo.webSite,
        extraCV: userContactInfo.extraCV,
        email: userContactInfo.email,
      };

      if (
        userContactInfo.userSocialNetworks &&
        userContactInfo.userSocialNetworks.length > 0 &&
        this.$refs.sociasNetworks
      ) {
        this.$refs.sociasNetworks.setSocials(
          userContactInfo.userSocialNetworks,
        );
      }
    },
    handleUpdateData() {
      this.$emit('update-data');
    },
    handleCancelEditing() {
      this.$router.push({
        name: 'UserProfile',
        params: {
          link: this.$route.params.link,
        },
      });
    },

    onImageChanged(selectedProfileFile) {
      this.selectedProfileFile = selectedProfileFile;
    },

    /**
     * Triggered when the avatar is changed.
     * Calls the `onImageChanged` method of the child `user-avatar` component.
     */
     onAvatarChanged(selectedAvatarFile) {
      // Call the `onImageChanged` method of `user-avatar` via ref
      if (this.$refs.userAvatar) {
        this.$refs.userAvatar.onNewFile(selectedAvatarFile);
      }
      this.selectedAvatarFile = selectedAvatarFile;
    },

    openAvatarFilePicker() {
      this.showAvatarModal = true;
    },
  },
  created() {
    const { userContactInfo } = this;

    if (userContactInfo) {
      this.setUserData();
    }
  },
  watch: {
    userContactInfo(newVal) {
      if (newVal) {
        this.setUserData();
      }
    },
  },
};
</script>

<style lang="scss">
@import '~@/styles/shared/_globals.scss';

.personal-data {
  &__title {
    margin: 0;
    margin: var(--global-dimension-size-250) 0 var(--global-dimension-size-200);

    font-weight: normal;
    font-size: var(--font-size-800);
    line-height: 1.6;
  }

  &__card {
    margin-bottom: 30px;
  }

  &__background {
    position: relative;
    min-width: 100%;
    height: 200px;

    img {
      position: absolute;
      width: 100%;
      height: 100%;

      object-fit: cover;
      object-position: center;
    }
  }

  &__avatar {
    position: relative;
    margin-top: -42px;
    margin-bottom: var(--global-dimension-size-300);
    margin-left: var(--global-dimension-size-300);
  }

  &__subtitle {
    margin-top: var(--global-dimension-size-200);
    margin-bottom: var(--global-dimension-size-200);

    font-size: var(--font-size-200);
    line-height: 1.6;
  }

  &__remove-social-button {
    margin-top: -10px;
    margin-bottom: var(--global-dimension-size-250);

    @include from('xl') {
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &__buttons {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
  }

  &__cancel-button {
    margin-right: auto;
  }
}
</style>
